
import React, { Component, useContext } from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';

import { Alert as AlertComponent, AlertProps as AlertComponentProps } from '@instructure/ui-alerts';
import { env } from '@/environment';
import { createPortal } from 'react-dom';

export const AlertsContext = React.createContext<AlertManager>(null);

export interface AlertOptions {
    level?: 'info' | 'success' | 'warning' | 'error';
    fixed?: boolean;
    timeout?: number;
    key?: string;
}

export class Alert {
    constructor(
        private manager: AlertManager,
        readonly message: React.ReactNode,
        readonly options: AlertOptions,
    ) {
        this.key = options.key || Math.random().toString(36).substr(2, 9);
        if (options.timeout == null) options.timeout = 5000;
        if (options.timeout) {
            this.timerId = setTimeout(() => {
                this.remove();
            }, options.timeout)
        }
    }

    readonly key: string;
    private timerId;

    @observable open = true;

    get level() { return this.options.level }
    get fixed() { return this.options.fixed }

    @action
    remove() {
        if (this.open) {
            this.open = false;
            this.clearTimeout();
            this.timerId = setTimeout(() => {
                this.forceRemove();
            }, 2000);
        }
    }

    forceRemove() {
        this.clearTimeout();
        this.manager._removeAlert(this);
    }

    clearTimeout() {
        if (this.timerId != null) {
            clearTimeout(this.timerId);
        }
    }
}

export class AlertManager {
    @observable currentAlerts: Alert[] = [];

    currentContainer = React.createRef<HTMLDivElement>();

    @action
    addAlert(message: React.ReactNode, options?: AlertOptions) {
        const alert = new Alert(this, message, options || {});
        this._removeAlert(alert.key);
        this.currentAlerts.push(alert);
        return alert;
    }

    push(message: React.ReactNode, options?: AlertOptions) {
        return this.addAlert(message, options);
    }

    removeAlert(alert: Alert | string) {
        if (typeof alert == 'string') {
            alert = this.currentAlerts.find((a) => a.key == alert);
        }
        if (alert) {
            alert.remove();
        }
    }

    @action
    _removeAlert(alert: Alert | string) {
        if (typeof alert != 'string') alert = alert.key;
        const i = this.currentAlerts.findIndex((a) => a.key == alert);
        if (i >= 0) {
            const matchedAlert = this.currentAlerts[i];
            matchedAlert.clearTimeout();
            this.currentAlerts.splice(i, 1);
        }
    }

    render() {
        return <AlertsContainer manager={this} />
    }
}

export const AlertContext = React.createContext<AlertManager>(null);

export const AlertsContainer: React.SFC<{ manager?: AlertManager }> = observer(({ manager: propsManager }) => {
    const contextManager = useContext(AlertContext);
    const manager = propsManager || contextManager;

    const issues = manager.currentAlerts || [];
    // if (!issues || issues.length == 0) return null;

    const issueComponents = issues.map(alert => {
        return <AlertComponent
            open={alert.open}
            variant={alert.level || "warning"}
            closeButtonLabel={alert.fixed ? null : "Close"}
            margin="small"
            key={alert.key}
            onDismiss={() => alert.remove()}
        >
            {alert.message}
        </AlertComponent>
    })
    return <div
        style={{ width: '600px', position: 'fixed', zIndex: 15000, left: '50%', transform: 'translateX(-50%)' }}
        ref={manager.currentContainer}
    >
        {issueComponents}
    </div>
})

export class AlertProvider extends Component<{ promptManager?: AlertManager }> {

    private _manager: AlertManager;
    get manager() {
        return this.props.promptManager || this._manager || (this._manager = new AlertManager())
    }

    render() {
        return <AlertContext.Provider value={this.manager} children={this.props.children} />
    }
}

export class DeclAlert extends Component<AlertComponentProps & { scroll?: boolean }> {
    static contextType = AlertContext;
    context: AlertManager;

    componentDidMount() {
        if (scroll) {
            window.parent.postMessage(JSON.stringify({
                subject: 'lti.scrollToTop',
                token: env.post_message_token,
            }), '*');
        }
    }

    render() {
        const { scroll, ...pass } = this.props;
        return createPortal(
            <AlertComponent {...pass} />,
            this.context.currentContainer.current
        )
    }
}
